import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function AdminSettings() {
  const { API } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [resetUserId, setResetUserId] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [globalMsg, setGlobalMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [u, f, r] = await Promise.all([
        API.get('/admin/users'),
        API.get('/admin/facilities'),
        API.get('/admin/regions'),
      ]);
      setUsers(u.data);
      setFacilities(f.data);
      setRegions(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doPasswordReset = async () => {
    if (!newPassword || newPassword.length < 8) {
      setPasswordMsg('Password must be at least 8 characters');
      return;
    }
    setPasswordSaving(true);
    setPasswordMsg('');
    try {
      await API.post(`/admin/users/${resetUserId}/reset-password`, { newPassword });
      setPasswordMsg('✅ Password updated successfully');
      setNewPassword('');
      setTimeout(() => { setResetUserId(null); setResetUser(null); setPasswordMsg(''); }, 2000);
    } catch (e) {
      setPasswordMsg('❌ Failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setPasswordSaving(false);
    }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name}? This cannot be undone.`)) return;
    try {
      await API.delete(`/admin/users/${id}`);
      setUsers(u => u.filter(x => x.id !== id));
    } catch (e) {
      alert('Failed to delete user: ' + (e.response?.data?.error || e.message));
    }
  };

  // Open the user edit modal. For maintenance/environmental staff we also
  // fetch their current coverage so the picker is pre-populated.
  const openUserEdit = async (u) => {
    const base = { ...u, firstName: u.first_name, lastName: u.last_name, isActive: u.is_active !== false, receiveAllTourAlerts: u.receive_all_tour_alerts === true, regionIds: (u.regions || []).map(r => r.id) };
    setForm(base);
    setModal('user');
    if (u.role === 'maintenance' || u.role === 'environmental') {
      try {
        const cov = await API.get(`/admin/users/${u.id}/coverage`);
        setForm(f => ({ ...f, coverageFacilityIds: cov.data.facilityIds || [], coverageRegionIds: cov.data.regionIds || [] }));
      } catch {
        // If coverage load fails, leave pickers empty — admin can re-select.
      }
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (modal === 'user') {
        let userId = form.id;
        if (form.id) {
          await API.put(`/admin/users/${form.id}`, {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            role: form.role,
            facilityId: form.facilityId,
            regionIds: form.role === 'regional_manager' ? (form.regionIds || []) : [],
            isActive: form.isActive !== false,
            receiveAllTourAlerts: form.receiveAllTourAlerts === true,
            title: form.title,
            department: form.department,
          });
        } else {
          const created = await API.post('/admin/users', form);
          userId = created.data.id;
        }
        // For maintenance/environmental staff, persist coverage selections.
        if ((form.role === 'maintenance' || form.role === 'environmental') && userId) {
          await API.put(`/admin/users/${userId}/coverage`, {
            facilityIds: form.coverageFacilityIds || [],
            regionIds: form.coverageRegionIds || [],
          });
        }
      } else if (modal === 'facility') {
        if (form.id) {
          await API.put(`/admin/facilities/${form.id}`, {
            name: form.name,
            address: form.address || '',
            regionId: form.regionId || null,
            adminIds: form.adminIds || [],
            isActive: form.isActive !== false,
            isTourable: form.is_tourable !== false,
          });
        } else {
          await API.post('/admin/facilities', {
            name: form.name,
            address: form.address || '',
            regionId: form.regionId || null,
            adminIds: form.adminIds || [],
            isActive: true,
            isTourable: form.is_tourable !== false,
          });
        }
      } else if (modal === 'region') {
        if (form.id) {
          await API.put(`/admin/regions/${form.id}`, form);
        } else {
          await API.post('/admin/regions', form);
        }
      }
      setGlobalMsg('Saved successfully');
      setModal(null);
      load();
    } catch (e) {
      setGlobalMsg('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    const roleNatural = (u.role || '').replace(/_/g, ' ');
    const regionNames = (u.regions || []).map(r => r.name).join(' ');
    return (
      ((u.first_name || '') + ' ' + (u.last_name || '')).toLowerCase().includes(s) ||
      u.email?.toLowerCase().includes(s) ||
      u.role?.toLowerCase().includes(s) ||
      roleNatural.toLowerCase().includes(s) ||
      u.facility_name?.toLowerCase().includes(s) ||
      regionNames.toLowerCase().includes(s) ||
      u.title?.toLowerCase().includes(s) ||
      u.department?.toLowerCase().includes(s)
    );
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h2>System Settings</h2>
          <p>Manage users, facilities, and regions</p>
        </div>
      </div>
      <div className="page-body">
        {globalMsg && (
          <div className={`alert-banner ${globalMsg.startsWith('Error') ? 'alert-danger' : 'alert-success'}`} style={{ marginBottom: 16 }}>
            {globalMsg} <button onClick={() => setGlobalMsg('')} style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 8 }}>✕</button>
          </div>
        )}

        <div className="tabs">
          <button className={`tab-btn ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>Users ({users.length})</button>
          <button className={`tab-btn ${tab === 'facilities' ? 'active' : ''}`} onClick={() => setTab('facilities')}>Facilities ({facilities.length})</button>
          <button className={`tab-btn ${tab === 'regions' ? 'active' : ''}`} onClick={() => setTab('regions')}>Regions ({regions.length})</button>
        </div>

        {/* USERS TAB */}
        {tab === 'users' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 className="card-title" style={{ marginBottom: 0 }}>Users</h3>
              <button className="btn btn-primary btn-sm" onClick={() => { setForm({}); setModal('user'); }}>+ Add User</button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <input className="form-control" placeholder="Search by name, email, role or facility..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 400 }} />
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th><th>Email</th><th>Role</th><th>Facility</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}</td>
                      <td style={{ fontSize: 13 }}>{u.email}</td>
                      <td><span className="badge badge-submitted" style={{ textTransform: 'capitalize' }}>{u.role.replace('_', ' ')}</span></td>
                      <td style={{ fontSize: 13, color: 'var(--gray-500)' }}>{u.facility_name || '—'}</td>
                      <td>{u.is_active ? <span style={{ color: 'var(--green)', fontSize: 12 }}>● Active</span> : <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>● Inactive</span>}</td>
                      <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openUserEdit(u)}>Edit</button>
                        <button className="btn btn-sm" style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }} onClick={() => { setResetUserId(u.id); setResetUser(u); setNewPassword(''); setPasswordMsg(''); }}>Reset Password</button>
                        <button className="btn btn-sm" style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }} onClick={() => deleteUser(u.id, u.first_name + ' ' + u.last_name)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* FACILITIES TAB */}
        {tab === 'facilities' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 className="card-title" style={{ marginBottom: 0 }}>Facilities</h3>
              <button className="btn btn-primary btn-sm" onClick={() => { setForm({}); setModal('facility'); }}>+ Add Facility</button>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Name</th><th>Address</th><th>Region</th><th>Admin</th><th>Tourable</th><th>Status</th><th></th></tr>
                </thead>
                <tbody>
                  {facilities.map(f => (
                    <tr key={f.id}>
                      <td style={{ fontWeight: 600 }}>{f.name}</td>
                      <td style={{ fontSize: 13, color: 'var(--gray-500)' }}>{f.address || '—'}</td>
                      <td style={{ fontSize: 13 }}>{f.region_name || '—'}</td>
                      <td style={{ fontSize: 13 }}>{f.admins && f.admins.length > 0 ? f.admins.map(a => a.name).join(', ') : '—'}</td>
                      <td style={{ fontSize: 13 }}>
                        {f.is_tourable === false
                          ? <span style={{ color: 'var(--gray-500)' }}>No</span>
                          : <span style={{ color: 'var(--green)' }}>Yes</span>}
                      </td>
                      <td>{f.is_active !== false ? <span style={{ color: 'var(--green)', fontSize: 12 }}>● Active</span> : <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>● Inactive</span>}</td>
                      <td>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setForm({ ...f, regionId: f.region_id, adminIds: (f.admins || []).map(a => a.id) }); setModal('facility'); }}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* REGIONS TAB */}
        {tab === 'regions' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 className="card-title" style={{ marginBottom: 0 }}>Regions</h3>
              <button className="btn btn-primary btn-sm" onClick={() => { setForm({}); setModal('region'); }}>+ Add Region</button>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Region Name</th><th>Regional Manager</th><th></th></tr>
                </thead>
                <tbody>
                  {regions.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600 }}>{r.name}</td>
                      <td>{r.manager_names || <span style={{ color: 'var(--amber)', fontWeight: 600 }}>⚠ none — escalations have no recipient</span>}</td>
                      <td><button className="btn btn-secondary btn-sm" onClick={() => { setForm({ ...r, regionalManagerId: r.regional_manager_id }); setModal('region'); }}>Edit</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PASSWORD RESET MODAL */}
        {resetUserId && (
          <div className="modal-overlay" onClick={() => setResetUserId(null)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
              <div className="modal-header">
                <h3>Reset Password</h3>
                <button className="modal-close" onClick={() => setResetUserId(null)}>×</button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16 }}>
                  Setting new password for <strong>{resetUser?.first_name} {resetUser?.last_name}</strong> ({resetUser?.email})
                </p>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input type="password" className="form-control" placeholder="Minimum 8 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && doPasswordReset()} autoFocus />
                </div>
                {passwordMsg && <div style={{ fontSize: 13, color: passwordMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)', marginTop: 8, fontWeight: 500 }}>{passwordMsg}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setResetUserId(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={doPasswordReset} disabled={passwordSaving}>{passwordSaving ? 'Updating...' : 'Update Password'}</button>
              </div>
            </div>
          </div>
        )}

        {/* ADD/EDIT MODAL */}
        {modal && (
          <div className="modal-overlay" onClick={() => setModal(null)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ overflowY: 'auto', display: 'block' }}>
              <h3 className="modal-title">{form.id ? 'Edit' : 'Add'} {modal === 'user' ? 'User' : modal === 'facility' ? 'Facility' : 'Region'}</h3>

              {modal === 'user' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="form-group">
                      <label className="form-label">First Name</label>
                      <input className="form-control" value={form.firstName || ''} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last Name</label>
                      <input className="form-control" value={form.lastName || ''} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-control" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                    {form.id && (
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                        Must match their Microsoft sign-in address exactly — SSO matches users by email. A mismatch will create a duplicate staff account on their next login.
                      </div>
                    )}
                  </div>
                  {!form.id && (
                    <div className="form-group">
                      <label className="form-label">Password</label>
                      <input type="password" className="form-control" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select className="form-control" value={form.role || ''} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                      <option value="">Select role</option>
                      {['staff', 'building_admin', 'regional_manager', 'executive_staff', 'super_admin', 'maintenance', 'environmental'].map(r => (
                        <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </div>
                  {form.role === 'regional_manager' && (
                  <div className="form-group">
                    <label className="form-label">Regions</label>
                    <div style={{ border: '1px solid var(--gray-300)', borderRadius: 6, padding: '8px', maxHeight: 150, overflowY: 'auto' }}>
                      {regions.map(r => (
                        <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 13 }}>
                          <input type="checkbox" checked={(form.regionIds || []).includes(r.id)} onChange={e => {
                            const ids = form.regionIds || [];
                            setForm(f => ({ ...f, regionIds: e.target.checked ? [...ids, r.id] : ids.filter(x => x !== r.id) }));
                          }} />
                          {r.name}
                        </label>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                      Regional managers receive 48-hour escalations and see reports for every facility in their checked regions.
                    </div>
                  </div>
                  )}
                  {form.id && (
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select
                        className="form-control"
                        value={form.isActive === false ? 'disabled' : 'active'}
                        onChange={e => setForm(f => ({ ...f, isActive: e.target.value === 'active' }))}
                      >
                        <option value="active">Active</option>
                        <option value="disabled">Disabled</option>
                      </select>
                      {form.isActive === false && (
                        <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>
                          Disabled users cannot sign in (password or Microsoft SSO) and any active session ends on their next page load. Their history and assignments are preserved.
                        </div>
                      )}
                    </div>
                  )}
                  {form.id && ['executive_staff', 'super_admin'].includes(form.role) && (
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                        <input
                          type="checkbox"
                          checked={form.receiveAllTourAlerts === true}
                          onChange={e => setForm(f => ({ ...f, receiveAllTourAlerts: e.target.checked }))}
                        />
                        Receive all tour notifications
                      </label>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                        Emails this user whenever any facility submits a tour, in addition to that facility's building admins. 72-hour executive escalations already go to all executive staff.
                      </div>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Facility</label>
                    <select className="form-control" value={form.facilityId || ''} onChange={e => setForm(f => ({ ...f, facilityId: e.target.value }))}>
                      <option value="">None</option>
                      {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                  </div>

                  {(form.role === 'maintenance' || form.role === 'environmental') && (
                    <div className="form-group">
                      <label className="form-label">
                        Coverage <span style={{ fontWeight: 400, color: 'var(--text-secondary)', fontSize: 12 }}>— which facilities/regions this person is alerted about</span>
                      </label>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Regions (covers all facilities in the region)</div>
                      <div style={{ border: '1px solid var(--gray-300)', borderRadius: 6, padding: '8px', maxHeight: 110, overflowY: 'auto', marginBottom: 10 }}>
                        {regions.map(r => (
                          <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 13 }}>
                            <input type="checkbox" checked={(form.coverageRegionIds || []).includes(r.id)} onChange={e => {
                              const ids = form.coverageRegionIds || [];
                              setForm(f => ({ ...f, coverageRegionIds: e.target.checked ? [...ids, r.id] : ids.filter(x => x !== r.id) }));
                            }} />
                            {r.name}
                          </label>
                        ))}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Individual facilities</div>
                      <div style={{ border: '1px solid var(--gray-300)', borderRadius: 6, padding: '8px', maxHeight: 150, overflowY: 'auto' }}>
                        {facilities.map(f => (
                          <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 13 }}>
                            <input type="checkbox" checked={(form.coverageFacilityIds || []).includes(f.id)} onChange={e => {
                              const ids = form.coverageFacilityIds || [];
                              setForm(fm => ({ ...fm, coverageFacilityIds: e.target.checked ? [...ids, f.id] : ids.filter(x => x !== f.id) }));
                            }} />
                            {f.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {modal === 'facility' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Facility Name</label>
                    <input className="form-control" value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Address</label>
                    <input className="form-control" value={form.address || ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Region</label>
                    <select className="form-control" value={form.regionId || ''} onChange={e => setForm(f => ({ ...f, regionId: e.target.value }))}>
                      <option value="">None</option>
                      {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Building Administrator(s)</label>
                    <div style={{ border: '1px solid var(--gray-300)', borderRadius: 6, padding: '8px', maxHeight: 150, overflowY: 'auto' }}>
                      {users.filter(u => u.role === 'building_admin').map(u => (
                        <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 13 }}>
                          <input type="checkbox" checked={(form.adminIds || []).includes(u.id)} onChange={e => {
                            const ids = form.adminIds || [];
                            setForm(f => ({ ...f, adminIds: e.target.checked ? [...ids, u.id] : ids.filter(x => x !== u.id) }));
                          }} />
                          {u.first_name} {u.last_name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tourable</label>
                    <select className="form-control" value={form.is_tourable === false ? 'no' : 'yes'} onChange={e => setForm(f => ({ ...f, is_tourable: e.target.value === 'yes' }))}>
                      <option value="yes">Yes — appears in tour-creation dropdowns</option>
                      <option value="no">No — hidden from tour dropdowns (e.g. corporate entities)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-control" value={form.isActive === false ? 'inactive' : 'active'} onChange={e => setForm(f => ({ ...f, isActive: e.target.value === 'active' }))}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </>
              )}

              {modal === 'region' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Region Name</label>
                    <input className="form-control" value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Regional Managers</label>
                    {form.id ? (
                      (() => {
                        const rms = users.filter(u =>
                          u.role === 'regional_manager' &&
                          u.is_active !== false &&
                          (u.regions || []).some(r => r.id === form.id)
                        );
                        return rms.length > 0 ? (
                          <div style={{ border: '1px solid var(--gray-300)', borderRadius: 6, padding: '8px 12px' }}>
                            {rms.map(u => (
                              <div key={u.id} style={{ fontSize: 13, padding: '3px 0' }}>
                                <span style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}</span>
                                <span style={{ color: 'var(--text-secondary)', marginLeft: 8, fontSize: 12 }}>{u.email}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: 13, color: 'var(--amber)', fontWeight: 600 }}>⚠️ No regional managers assigned — escalations for this region have no recipient.</div>
                        );
                      })()
                    ) : (
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Save the region first, then assign managers.</div>
                    )}
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                      To add or remove managers: edit the user (Users tab) and check this region in their <strong>Regions</strong> list. A region can have any number of managers — all receive escalation alerts.
                    </div>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
                <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
